import React, { FC, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { FinishData } from 'shared/types/product/Product';
import Typography from 'components/commercetools-ui/atoms/typography';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import useClassNames from 'helpers/hooks/useClassNames';
import { useFormat } from 'helpers/hooks/useFormat';
import useI18n from 'helpers/hooks/useI18n';
import usePath from 'helpers/hooks/usePath';
import { textToColor } from 'helpers/textToColor/textToColor';
import { getVariantLeadTime } from 'helpers/utils/getVariantLeadTime';
import { replaceTemplateString } from 'helpers/utils/replaceTemplateString';
import { Money } from 'types/entity/product';
import { ProductDetailsProps } from '..';
import ProductAttribute from './product-attribute';
import { Wirebrush } from './wirebrush';
import { WoodFinishHelper } from './wood-finish-helper';
import { DEFAULT_LOCALE } from "../../../../../../helpers/constants/locale";
import { ProductJSONLD } from '../../../../../../helpers/structuredData/product';
import { useFinishPrice } from '../hooks/useFinishPrice';
import Rating from 'components/commercetools-ui/atoms/rating';

// Create a reviews link that points to the Reviews component section using a hash anchor
const reviewsLink = '#reviews';

type ProductInformationProps = Omit<ProductDetailsProps, 'onAddToCart' | 'content'> & {
  finish?: FinishData;
  onChangeFinish: (finish: string) => void;
  finishOptions?: string[];
  sheen?: string;
  plainTextDescription?: string;
  onChangeSheen: (sheen: string) => void;
  sheenOptions?: string[];
  quantity: number;
  origin?: string;
  onChangeWirebrush?: (isChecked: boolean) => void;
};

const ProductInformation: FC<ProductInformationProps> = ({
  product,
  variant,
  quantity,
  plainTextDescription,
  onChangeVariant,
  inModalVersion,
  wirebrushModal,
  woodFinishHelper,
  finish,
  finishOptions,
  onChangeFinish,
  sheen,
  sheenOptions,
  onChangeSheen,
  onChangeWirebrush,
  reviewListProps,
  origin
}) => {
  const router = useRouter();
  const { locale } = useI18n();
  const { path } = usePath();
  const searchParams = useSearchParams();

  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });

  // Object with currently selected attribute values.
  // E.g. { length: "3 feet long", plow: "Standard" }
  const [selectedOptions, setSelectedOptions] = useState(() => {
    const selectedOptions: Record<string, string> = {};
    product.variantDefiningAttributes?.forEach((att) => (selectedOptions[att.name] = variant.attributes?.[att.name]));
    return selectedOptions;
  });

  const discountPercentage =
    variant.discountedPrice &&
    (((variant.price?.centAmount as number) - (variant.discountedPrice.centAmount as number)) /
      (variant.price?.centAmount as number)) *
      100;

  const updateVariantSKU = (sku: string) => {
    const params = new URLSearchParams(searchParams.toString());
    router.replace(`/${path.split('/').filter(Boolean).slice(0, -1).join('/')}/${sku}?${params.toString()}`, {
      scroll: false,
    });
  };

  const titleClassName = useClassNames(['break-normal mb-12', { 'body-text-x-large font-medium': !inModalVersion }]);
  const priceClassName = useClassNames([
    'description text-text-secondary font-bold',
    { 'line-through text-text-tertiary': !!discountPercentage },
    { '2xl:text-20': !inModalVersion },
    { 'text-14': !!inModalVersion && !discountPercentage },
    { 'text-12': !!inModalVersion && !!discountPercentage },
  ]);
  const discountedPriceClassName = useClassNames([
    'block font-bold leading-loose text-text-secondary',
    { 'body-text-x-large': !inModalVersion },
  ]);

  const hasPrice = variant.price?.centAmount;
  const finishPrice = useFinishPrice(variant, quantity, finish);

  const finalBasePrice: Money | undefined = hasPrice
    ? {
        ...variant.price,
        centAmount: (variant.price?.centAmount ?? 0) + (finishPrice?.finishPrice?.centAmount ?? 0),
      }
    : undefined;

  const businessDaysText = formatProductMessage({
    id: 'businessDays',
    defaultMessage: '{leadTime} business day(s)',
  });
  const formattedBusinessDays = replaceTemplateString(businessDaysText, { leadTime: getVariantLeadTime(variant) });

  return (
    <div>
      <ProductJSONLD
        product={product}
        variant={variant}
        description={plainTextDescription}
        origin={origin}
        locale={DEFAULT_LOCALE}
        finalBasePrice={finalBasePrice?.centAmount ?? 0}
        ratingData={product?.reviewRatingStatistics}
        reviews={reviewListProps}
      />
      <div className="relative flex flex-col pr-40">
        <h1 className="sr-only">{variant.name ?? product.name}</h1>
        <Typography as="h2" className={titleClassName}>
          {variant.name ?? product?.name}
        </Typography>
        <p className="body-text text-text-tertiary">
          {formatProductMessage({ id: 'sku', defaultMessage: 'SKU' })}: {variant.sku}
        </p>
        {/* Add Rating component if review statistics are available */}
        {product.reviewRatingStatistics?.averageRating && product.reviewRatingStatistics?.count && (
          <div className="mt-8">
            <Rating
              rating={product.reviewRatingStatistics.averageRating}
              reviewCount={product.reviewRatingStatistics.count}
              reviewsLink={reviewsLink}
              textSize="large"
            />
          </div>
        )}
      </div>
      {hasPrice && discountPercentage ? (
        <div className="mb-32 mt-12 flex flex-row items-center justify-between">
          <div className="flex items-center gap-8">
            <Typography className={discountedPriceClassName}>
              {CurrencyHelpers.formatForCurrency(variant.discountedPrice as Money, locale)}
            </Typography>
            <Typography className={priceClassName}>
              {CurrencyHelpers.formatForCurrency(finalBasePrice as Money, locale)}
            </Typography>
          </div>

          {
            <span className="flex h-25 w-45 items-center justify-center bg-accent-red text-11 text-text-inverted">
              {Math.round(discountPercentage)}%
            </span>
          }
        </div>
      ) : hasPrice ? (
        <div className="mb-32 mt-16 flex items-center gap-8">
          <Typography className="body-text-x-large font-normal text-text-tertiary">
            {formatProductMessage({ id: 'list.price', defaultMessage: 'List price' })}
          </Typography>
          <Typography className={priceClassName}>
            {CurrencyHelpers.formatForCurrency(finalBasePrice as Money, locale)}
          </Typography>
        </div>
      ) : null}

      {product.finishes && finishOptions && finish && (
        <ProductAttribute
          className="mt-24"
          options={finishOptions}
          currentVariant={variant}
          isColorAttribute
          selectedOption={`${finish?.name}:${finish?.hex}`}
          attribute="finish"
          inModalVersion={inModalVersion}
          onClick={(_attribute, option) => {
            onChangeFinish(textToColor(option).label);
          }}
        />
      )}
      {product.isWoodProduct && woodFinishHelper?.text && <WoodFinishHelper className="mt-8" {...woodFinishHelper} />}

      {product.sheens && sheenOptions && sheen && (
        <ProductAttribute
          className="mt-24"
          options={sheenOptions}
          currentVariant={variant}
          selectedOption={sheen}
          attribute="sheen"
          inModalVersion={inModalVersion}
          onClick={(_attribute, option) => {
            onChangeSheen(option);
          }}
        />
      )}

      {product.useWirebrush && onChangeWirebrush && wirebrushModal && (
        <Wirebrush
          modal={wirebrushModal}
          className="mt-20"
          checkbox={{
            label: 'Wirebrush',
            name: 'wirebrush',
            onChange: ({ checked }) => {
              onChangeWirebrush(checked);
            },
          }}
        />
      )}

      {product.variantDefiningAttributes &&
        product.variantDefiningAttributes.map((attribute) => {
          return (
            <ProductAttribute
              key={attribute.name}
              className="mt-24"
              options={product.variantsMap?.[variant.sku][attribute.name] ?? []}
              currentVariant={variant}
              attribute={attribute.name}
              isColorAttribute={attribute.isColorAttribute}
              inModalVersion={inModalVersion}
              onClick={(attribute, option) => {
                const newSelectedOptions = { ...selectedOptions, [attribute]: option };
                setSelectedOptions(newSelectedOptions);
                const matchingVariant = product.variantDefiningAttributeGroups?.find((variant) =>
                  Object.entries(newSelectedOptions).every(([attrKey, attrValue]) => {
                    return variant[attrKey] === attrValue;
                  }),
                );
                const sku = matchingVariant?.sku;
                if (sku) inModalVersion ? onChangeVariant(sku) : updateVariantSKU(sku);
              }}
            />
          );
        })}

      <div className="mt-24 flex flex-col gap-16">
        <div className="flex items-center gap-8">
          <img src="/icons/tailwind-icons/icon-truck.svg" alt="Shipping" className="size-16" />
          <p className="body-text-x-small text-text-tertiary">
            {formatProductMessage({ id: 'shipsInAbout', defaultMessage: 'Ships in about' })}{' '}
            <span className="font-medium">{formattedBusinessDays}</span>
          </p>
        </div>
        {variant.isAvailable && (
          <div className="flex items-center gap-8">
            <img src="/icons/tailwind-icons/icon-stock.svg" alt="Stock Status" className="size-16" />
            <Typography className={`body-text-small font-medium text-text-success`}>
              {formatProductMessage({ id: 'inStock' })}
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductInformation;
